import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createProviderConfig,
  deleteProviderConfig,
  listProviderConfigs,
  updateProviderConfig,
  type CreateProviderConfigInput,
  type FxStrategy,
  type Provider,
  type ProviderConfig,
  type ProviderMode,
} from "../api/providerConfigs";
import { listProviders, type ProviderCatalogEntry } from "../api/providers";

const PROVIDER_LABEL: Record<Provider, string> = {
  dlocal: "dLocal Go",
  dpago: "Dpago",
  bancard: "Bancard",
  pagopar: "Pagopar",
  dinelco: "Dinelco / Infonet",
};

const MODE_LABEL: Record<ProviderMode, string> = {
  sandbox: "Pruebas",
  live: "Producción",
};

const FX_LABEL: Record<FxStrategy, string> = {
  reject_others: "Rechazar otras monedas",
  delegate_provider: "Delegar al provider",
  freeze_own: "Congelar tipo de cambio propio",
};

// Campos esperados por provider — usados sólo para guiar al usuario.
type CredentialHint = {
  key: string;
  label: string;
  secret?: boolean;
  optional?: boolean;
  helper?: string;
};
const CREDENTIAL_HINTS: Record<Provider, CredentialHint[]> = {
  dlocal: [
    { key: "apiKey", label: "API Key" },
    { key: "secretKey", label: "Secret Key", secret: true },
  ],
  dpago: [
    { key: "publicToken", label: "Token público", helper: "pk_… (firma el request)" },
    { key: "privateToken", label: "Token privado", secret: true, helper: "sk_… (valida el callback)" },
    { key: "commerceId", label: "Commerce ID", helper: "ID del comercio en Dpago (ej. 829)" },
    {
      key: "defaultPlatformId",
      label: "Medio por defecto (platformId)",
      optional: true,
      helper: "Opcional. Ej. 18 = QR Ueno. Se usa si el pago no especifica método.",
    },
  ],
  bancard: [
    { key: "publicKey", label: "Public Key" },
    { key: "privateKey", label: "Private Key", secret: true },
  ],
  pagopar: [
    { key: "publicKey", label: "Public Key" },
    { key: "privateKey", label: "Private Key", secret: true },
  ],
  dinelco: [
    { key: "username", label: "Usuario" },
    { key: "password", label: "Contraseña", secret: true },
  ],
};

interface Props {
  merchantId: string;
}

export function ProviderConfigsSection({ merchantId }: Props) {
  const [configs, setConfigs] = useState<ProviderConfig[]>([]);
  const [catalog, setCatalog] = useState<ProviderCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderConfig | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProviderConfig | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, cat] = await Promise.all([
        listProviderConfigs(merchantId),
        listProviders(),
      ]);
      setConfigs(list.data);
      setCatalog(cat.providers);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error cargando pasarelas");
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleToggleActive(cfg: ProviderConfig) {
    try {
      const updated = await updateProviderConfig(merchantId, cfg.id, {
        active: !cfg.active,
      });
      setConfigs((prev) => prev.map((c) => (c.id === cfg.id ? updated : c)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar");
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    try {
      await deleteProviderConfig(merchantId, pendingDelete.id);
      setConfigs((prev) => prev.filter((c) => c.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al eliminar");
    }
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Acá registrás las cuentas que el comercio creó en cada pasarela (dLocal, Bancard, etc.).
        Novasis Pay usa estas credenciales para procesar los cobros del comercio. Las claves se
        almacenan encriptadas y nunca se devuelven en la API.
      </Alert>

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          Agregar pasarela
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : configs.length === 0 ? (
        <Alert severity="warning">
          Este comercio todavía no tiene pasarelas asociadas. Sin esto, no podrá cobrar.
        </Alert>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pasarela</TableCell>
                <TableCell>País</TableCell>
                <TableCell>Ambiente</TableCell>
                <TableCell>FX</TableCell>
                <TableCell align="center">Prioridad</TableCell>
                <TableCell>Credenciales</TableCell>
                <TableCell align="center">Activa</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((cfg) => (
                <TableRow key={cfg.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {PROVIDER_LABEL[cfg.provider] ?? cfg.provider}
                    </Typography>
                  </TableCell>
                  <TableCell>{cfg.country}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={MODE_LABEL[cfg.mode]}
                      color={cfg.mode === "live" ? "warning" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {FX_LABEL[cfg.fxStrategy]}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{cfg.priority}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={cfg.hasCredentials ? "Cargadas" : "Faltan"}
                      color={cfg.hasCredentials ? "success" : "error"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      size="small"
                      checked={cfg.active}
                      onChange={() => handleToggleActive(cfg)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar / regenerar credenciales">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditing(cfg);
                          setDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => setPendingDelete(cfg)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <ProviderConfigDialog
        open={dialogOpen}
        editing={editing}
        catalog={catalog}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSaved={(saved) => {
          setConfigs((prev) => {
            const idx = prev.findIndex((c) => c.id === saved.id);
            return idx >= 0
              ? prev.map((c) => (c.id === saved.id ? saved : c))
              : [saved, ...prev];
          });
          setDialogOpen(false);
          setEditing(null);
        }}
        merchantId={merchantId}
      />

      <Dialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Eliminar pasarela</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            La pasarela dejará de estar disponible para este comercio. Los cobros en curso
            no se afectan, pero no podrá crear nuevos a través de esta config.
          </Typography>
          {pendingDelete && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>{PROVIDER_LABEL[pendingDelete.provider]}</strong> · {pendingDelete.country} ·{" "}
              {MODE_LABEL[pendingDelete.mode]}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

interface DialogProps {
  open: boolean;
  editing: ProviderConfig | null;
  catalog: ProviderCatalogEntry[];
  merchantId: string;
  onClose: () => void;
  onSaved: (cfg: ProviderConfig) => void;
}

function ProviderConfigDialog({
  open,
  editing,
  catalog,
  merchantId,
  onClose,
  onSaved,
}: DialogProps) {
  const [provider, setProvider] = useState<Provider>("dlocal");
  const [mode, setMode] = useState<ProviderMode>("sandbox");
  const [country, setCountry] = useState("PY");
  const [fxStrategy, setFxStrategy] = useState<FxStrategy>("delegate_provider");
  const [priority, setPriority] = useState(0);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setProvider(editing.provider);
      setMode(editing.mode);
      setCountry(editing.country);
      setFxStrategy(editing.fxStrategy);
      setPriority(editing.priority);
      setCredentials({});
    } else {
      setProvider("dlocal");
      setMode("sandbox");
      setCountry("PY");
      setFxStrategy("delegate_provider");
      setPriority(0);
      setCredentials({});
    }
    setError(null);
  }, [open, editing]);

  const hints = useMemo(() => CREDENTIAL_HINTS[provider] ?? [], [provider]);
  const availableProviders = useMemo<Provider[]>(
    () =>
      (catalog.length > 0
        ? catalog.filter((p) => p.available).map((p) => p.id as Provider)
        : (["dlocal", "bancard", "pagopar", "dinelco"] as Provider[])),
    [catalog],
  );
  const countryOptions = useMemo(() => {
    const entry = catalog.find((p) => p.id === provider);
    return entry?.countries ?? ["PY", "UY", "AR", "BR"];
  }, [catalog, provider]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      // Los campos opcionales (ej. defaultPlatformId de Dpago) no bloquean el guardado.
      const requiredHints = hints.filter((h) => !h.optional);
      const credsFilled = requiredHints.every((h) => credentials[h.key]?.trim());
      if (!editing && !credsFilled) {
        throw new Error("Cargá todas las credenciales antes de guardar.");
      }
      if (editing) {
        const body = {
          fxStrategy,
          priority,
          ...(credsFilled ? { credentials } : {}),
        };
        const updated = await updateProviderConfig(merchantId, editing.id, body);
        onSaved(updated);
      } else {
        const body: CreateProviderConfigInput = {
          provider,
          mode,
          country,
          fxStrategy,
          priority,
          credentials,
        };
        const created = await createProviderConfig(merchantId, body);
        onSaved(created);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? "Editar pasarela" : "Agregar pasarela"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Pasarela"
              value={provider}
              onChange={(e) => setProvider(e.target.value as Provider)}
              fullWidth
              disabled={!!editing}
            >
              {availableProviders.map((p) => (
                <MenuItem key={p} value={p}>
                  {PROVIDER_LABEL[p]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Ambiente"
              value={mode}
              onChange={(e) => setMode(e.target.value as ProviderMode)}
              fullWidth
              disabled={!!editing}
            >
              <MenuItem value="sandbox">Pruebas (sandbox)</MenuItem>
              <MenuItem value="live">Producción</MenuItem>
            </TextField>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="País"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              fullWidth
              disabled={!!editing}
            >
              {countryOptions.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Prioridad"
              type="number"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              fullWidth
              helperText="Mayor = preferido"
            />
          </Stack>

          <TextField
            select
            label="Estrategia FX"
            value={fxStrategy}
            onChange={(e) => setFxStrategy(e.target.value as FxStrategy)}
            fullWidth
          >
            {(Object.keys(FX_LABEL) as FxStrategy[]).map((k) => (
              <MenuItem key={k} value={k}>
                {FX_LABEL[k]}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Credenciales {editing && "(opcional — dejar vacío para mantener actuales)"}
            </Typography>
            <Stack spacing={1.5}>
              {hints.map((h) => (
                <TextField
                  key={h.key}
                  label={h.optional ? `${h.label} (opcional)` : h.label}
                  type={h.secret ? "password" : "text"}
                  value={credentials[h.key] ?? ""}
                  onChange={(e) =>
                    setCredentials((prev) => ({ ...prev, [h.key]: e.target.value }))
                  }
                  helperText={h.helper}
                  fullWidth
                  autoComplete="off"
                />
              ))}
            </Stack>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear pasarela"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
